import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, ArrowLeft, MapPin, Mail, Phone, Globe, Calendar, Kanban, Copy, Loader2, FileText } from "lucide-react";
import { toast } from "sonner";
import QuoteGeneratorDialog from "@/components/QuoteGeneratorDialog";

const MSG_TYPES = [
  { v: "email_professional", l: "Courriel pro" },
  { v: "email_short", l: "Courriel court" },
  { v: "email_followup", l: "Relance" },
  { v: "phone_script", l: "Script d'appel" },
  { v: "linkedin_message", l: "LinkedIn" },
  { v: "sms", l: "SMS" },
];

const evidenceColor = (t) => ({
  FAIT: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  INFÉRENCE: "bg-cyan-500/10 text-cyan-300 border-cyan-500/30",
  ESTIMATION: "bg-amber-500/10 text-amber-300 border-amber-500/30",
}[t] || "bg-secondary text-foreground");

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [qualifying, setQualifying] = useState(false);
  const [outreach, setOutreach] = useState(null);
  const [outreachType, setOutreachType] = useState("email_professional");
  const [genLoading, setGenLoading] = useState(false);
  const [addingCrm, setAddingCrm] = useState(false);
  const [quoteOpen, setQuoteOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/opportunities/${id}`);
    setOpp(data);
  };

  useEffect(() => { load().catch(() => toast.error("Opportunité introuvable")); }, [id]);

  const runQualify = async () => {
    setQualifying(true);
    try {
      await api.post(`/opportunities/${id}/qualify`);
      await load();
      toast.success("Analyse IA terminée");
    } catch { toast.error("Erreur d'analyse IA"); }
    finally { setQualifying(false); }
  };

  const generate = async () => {
    setGenLoading(true);
    setOutreach(null);
    try {
      const { data } = await api.post("/ai/outreach", { opportunity_id: id, message_type: outreachType });
      setOutreach(data);
      toast.success("Message généré");
    } catch { toast.error("Erreur de génération"); }
    finally { setGenLoading(false); }
  };

  const addToCRM = async () => {
    setAddingCrm(true);
    try {
      const { data } = await api.post(`/crm/leads/${id}`);
      toast.success(data.already_added ? "Déjà dans votre CRM" : "Ajouté au CRM");
    } catch { toast.error("Erreur"); }
    finally { setAddingCrm(false); }
  };

  const copyText = (t) => { navigator.clipboard.writeText(t); toast.success("Copié"); };

  if (!opp) return <div className="text-muted-foreground">Chargement…</div>;

  return (
    <div className="space-y-6" data-testid="opportunity-detail-page">
      <Link to="/opportunites" className="text-sm text-cyan-400 hover:underline flex items-center gap-1"><ArrowLeft className="h-3 w-3" /> Retour aux opportunités</Link>

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Badge className="bg-cyan-500/10 text-cyan-300 border-cyan-500/30 mb-2">{opp.opportunity_type.replace(/_/g, " ")}</Badge>
          <h1 className="font-display text-3xl font-bold tracking-tight">{opp.company_name}</h1>
          <div className="text-muted-foreground flex flex-wrap items-center gap-3 mt-2 text-sm">
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {opp.location}</span>
            <span>·</span>
            <span>{opp.industry}</span>
            {opp.building_type && <><span>·</span><span>{opp.building_type}</span></>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setQuoteOpen(true)} data-testid="open-quote-dialog-button">
            <FileText className="h-4 w-4 mr-2" /> Générer soumission
          </Button>
          <Button variant="outline" onClick={addToCRM} disabled={addingCrm} data-testid="add-to-crm-button"><Kanban className="h-4 w-4 mr-2" /> Ajouter au CRM</Button>
          <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" onClick={runQualify} disabled={qualifying} data-testid="qualify-ai-button">
            {qualifying ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyse…</> : <><Sparkles className="h-4 w-4 mr-2" /> Analyser avec IA</>}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* AI Score card */}
          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold">Score d'opportunité IA</h3>
              <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">{opp.ai_priority || "Non analysé"}</Badge>
            </div>
            <div className="flex items-end gap-3 mb-4">
              <div className="font-display text-6xl font-bold text-gradient-cyan">{opp.ai_score || 0}</div>
              <div className="text-sm text-muted-foreground mb-2">/ 100 · basé sur les preuves disponibles</div>
            </div>
            <div className="space-y-2">
              {(opp.ai_reasons || []).map((r, i) => (<div key={i} className="text-sm flex items-start gap-2"><span className="text-cyan-400 mt-0.5">•</span> {r}</div>))}
            </div>
          </Card>

          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold mb-3">Description</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{opp.description}</p>
          </Card>

          <Card className="p-6 bg-card border-border">
            <h3 className="font-display font-semibold mb-4">Preuves & niveau de confiance</h3>
            <div className="space-y-3">
              {(opp.ai_evidence || []).map((e, i) => (
                <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/40 border border-border">
                  <Badge className={`${evidenceColor(e.type)} text-[10px] shrink-0`}>{e.type}</Badge>
                  <div className="text-sm text-foreground">{e.text}</div>
                </div>
              ))}
              {!(opp.ai_evidence || []).length && <div className="text-sm text-muted-foreground">Lancez l'analyse IA pour classer les preuves (FAIT / INFÉRENCE / ESTIMATION).</div>}
            </div>
          </Card>

          {/* AI Outreach */}
          <Card className="p-6 bg-card border-border">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-400" /> Générateur d'approche IA</h3>
            </div>
            <Tabs value={outreachType} onValueChange={setOutreachType}>
              <TabsList className="grid grid-cols-3 md:grid-cols-6 mb-4 bg-secondary/40">
                {MSG_TYPES.map((t) => (<TabsTrigger key={t.v} value={t.v} data-testid={`outreach-type-${t.v}`} className="text-xs">{t.l}</TabsTrigger>))}
              </TabsList>
              <div className="flex justify-end mb-4">
                <Button className="bg-cyan-500 hover:bg-cyan-400 text-slate-950" onClick={generate} disabled={genLoading} data-testid="generate-outreach-button">
                  {genLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Génération…</> : <><Sparkles className="h-4 w-4 mr-2" /> Générer</>}
                </Button>
              </div>
              <TabsContent value={outreachType} className="mt-0">
                {outreach ? (
                  <div className="space-y-3">
                    {outreach.subject && (
                      <div className="p-3 rounded-lg bg-secondary/40 border border-border">
                        <div className="text-[10px] uppercase text-muted-foreground font-mono mb-1">Sujet</div>
                        <div className="text-sm font-medium">{outreach.subject}</div>
                      </div>
                    )}
                    <div className="p-4 rounded-lg bg-secondary/40 border border-border relative">
                      <Button size="icon" variant="ghost" className="absolute top-2 right-2 h-7 w-7" onClick={() => copyText(outreach.body || "")}><Copy className="h-3.5 w-3.5" /></Button>
                      <div className="text-sm whitespace-pre-wrap leading-relaxed pr-8" data-testid="outreach-message-body">{outreach.body}</div>
                    </div>
                    <p className="text-xs text-muted-foreground">Éditez avant l'envoi. Aucun message n'est envoyé sans votre autorisation explicite.</p>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center border border-dashed border-border rounded-lg">
                    Sélectionnez un type et cliquez sur Générer.
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card className="p-5 bg-card border-border">
            <h4 className="font-display font-semibold mb-3 text-sm">Coordonnées</h4>
            <div className="space-y-2 text-sm">
              {opp.contact_email && <div className="flex items-center gap-2"><Mail className="h-3.5 w-3.5 text-muted-foreground" /> {opp.contact_email}</div>}
              {opp.contact_phone && <div className="flex items-center gap-2"><Phone className="h-3.5 w-3.5 text-muted-foreground" /> {opp.contact_phone}</div>}
              {opp.website && <div className="flex items-center gap-2"><Globe className="h-3.5 w-3.5 text-muted-foreground" /> <a href={opp.website} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline truncate">{opp.website}</a></div>}
              {opp.deadline && <div className="flex items-center gap-2"><Calendar className="h-3.5 w-3.5 text-muted-foreground" /> Échéance: {new Date(opp.deadline).toLocaleDateString("fr-CA")}</div>}
              {!opp.contact_email && !opp.contact_phone && !opp.website && <div className="text-muted-foreground text-xs">Aucun contact public disponible. Recherche à effectuer.</div>}
            </div>
          </Card>

          <Card className="p-5 bg-card border-border">
            <h4 className="font-display font-semibold mb-3 text-sm">Source & vérification</h4>
            <div className="space-y-2 text-xs text-muted-foreground">
              <div>Source: <span className="text-foreground font-medium">{opp.source}</span></div>
              {opp.source_url && <div>URL: <a href={opp.source_url} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline break-all">{opp.source_url}</a></div>}
              <div>Découvert: {new Date(opp.created_at).toLocaleDateString("fr-CA")}</div>
              <div>Vérifié: {new Date(opp.last_verified || opp.created_at).toLocaleDateString("fr-CA")}</div>
              {opp.is_demo && <Badge className="bg-amber-500/10 text-amber-300 border-amber-500/30 mt-2">DEMO DATA</Badge>}
            </div>
          </Card>

          {opp.ai_next_action && (
            <Card className="p-5 bg-card border-border">
              <h4 className="font-display font-semibold mb-2 text-sm">Prochaine action recommandée</h4>
              <p className="text-sm text-cyan-300">{opp.ai_next_action}</p>
            </Card>
          )}

          {(opp.ai_suggested_services || []).length > 0 && (
            <Card className="p-5 bg-card border-border">
              <h4 className="font-display font-semibold mb-3 text-sm">Services suggérés</h4>
              <div className="flex flex-wrap gap-1.5">
                {opp.ai_suggested_services.map((s, i) => <Badge key={i} variant="outline" className="text-[10px]">{s}</Badge>)}
              </div>
            </Card>
          )}
        </div>
      </div>
      <QuoteGeneratorDialog
        opportunityId={id}
        companyName={opp.company_name}
        open={quoteOpen}
        onOpenChange={setQuoteOpen}
      />
    </div>
  );
}
